import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

// SHARED manager timeline — created / acknowledged / responded / resolved,
// each with its timestamp and who did it. Reads the record's own milestones
// plus the platform action events; it NEVER reads anyone's view preferences,
// so a staff member's dismissal cannot change what a manager sees.
const STEP_COLOR = {
    created: 'bg-slate-400', acknowledged: 'bg-amber-400', responded: 'bg-sky-400',
    resolved: 'bg-emerald-400', cancelled: 'bg-rose-400', reassign: 'bg-violet-400',
};

const _deptLabel = (L, id) => (id ? (L.dept?.[id] || String(id).replace(/_/g, ' ')) : '');

export default function ActionTimeline({ fetcher, labels, testid = 'action-timeline' }) {
    const L = labels || {};
    const [state, setState] = useState({ loading: true, steps: [] });

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const d = await fetcher();
                if (alive) setState({ loading: false, steps: d?.steps || [] });
            } catch {
                if (alive) setState({ loading: false, steps: [] });
            }
        })();
        return () => { alive = false; };
    }, [fetcher]);

    if (state.loading) {
        return <div className="flex items-center gap-2 text-xs text-slate-400 py-2" data-testid={`${testid}-loading`}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />{L.loading}
        </div>;
    }
    if (!state.steps.length) {
        return <p className="text-xs text-slate-500 py-2" data-testid={`${testid}-empty`}>{L.empty}</p>;
    }

    return (
        <div data-testid={testid}>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">{L.title}</p>
            <ol className="space-y-2">
                {state.steps.map((s, i) => (
                    <li key={`${s.step}-${i}`} className="flex items-start gap-2.5"
                        data-testid={`${testid}-step-${s.step}`}>
                        <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${STEP_COLOR[s.step] || 'bg-slate-500'}`} />
                        <div className="min-w-0">
                            <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 capitalize">
                                {L.step?.[s.step] || (s.step || '').replace(/_/g, ' ')}
                                {s.step === 'reassign' && (s.from_department || s.to_department) && (
                                    <span className="ml-1.5 font-normal normal-case text-violet-600 dark:text-violet-300"
                                        data-testid={`${testid}-hop-${i}`}>
                                        {_deptLabel(L, s.from_department)} → {_deptLabel(L, s.to_department)}
                                        {s.to_person_name ? ` · ${s.to_person_name}` : ''}
                                    </span>
                                )}
                                <span className="ml-2 font-normal text-slate-500 dark:text-slate-400">
                                    {s.at ? new Date(s.at).toLocaleString() : ''}
                                </span>
                            </p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                {s.actor_name
                                    ? `${L.by || 'by'} ${s.actor_name}`
                                    : <span className="italic">{L.noActor}</span>}
                                {s.notes ? ` · ${s.notes}` : ''}
                            </p>
                        </div>
                    </li>
                ))}
            </ol>
        </div>
    );
}
